import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, Server, Cloud, ArrowRight, CheckCircle, Users, Award, Headphones, Sparkles, Heart, Lightbulb, Handshake, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SectionHeading from "@/components/ui/SectionHeading";
import { FirewallAdvisorModal } from "@/components/products/FirewallAdvisorModal";
import FirewallAdvisorFAB from "@/components/FirewallAdvisorFAB";
import { usePageSections, getExtraDataItems } from "@/hooks/usePageContent";
import { useLanguage } from "@/contexts/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";
import heroCybersecurity from "@/assets/hero-cybersecurity.jpg";
import teamMeeting from "@/assets/team-meeting.jpg";
import datacenter from "@/assets/datacenter.jpg";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Shield,
  Lock,
  Server,
  Cloud,
  Users,
  Award,
  Headphones,
  Heart,
  Lightbulb,
  Handshake,
  Clock,
};

interface StatItem {
  value: string;
  label: string;
}

interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}

const Index = () => {
  const [advisorModalOpen, setAdvisorModalOpen] = useState(false);
  const { data: sections, isLoading } = usePageSections("home");
  const { currentLanguage } = useLanguage();

  const languagePrefix = currentLanguage === "nl" ? "" : `/${currentLanguage}`;

  // Fallback content
  const fallback = {
    hero: {
      title: "CYBERSECURITY OPLOSSINGEN VOOR HET MKB EN ENTERPRISE",
      subtitle: "Officieel Stormshield Distributeur & Darktrace Partner",
      content: "Cyscom is uw betrouwbare partner voor Stormshield en Darktrace cybersecurity oplossingen.",
      button_text: "Word Partner",
      button_link: "/partner-worden",
    },
    stats: [
      { value: "15+", label: "Jaar Ervaring" },
      { value: "500+", label: "Tevreden Klanten" },
      { value: "24/7", label: "Support" },
      { value: "100%", label: "Benelux Dekking" },
    ],
    features: [
      { icon: "Shield", title: "Network Security", description: "Geavanceerde firewalls en netwerk beveiligingsoplossingen." },
      { icon: "Lock", title: "Endpoint Protection", description: "Bescherm al uw endpoints tegen malware en ransomware." },
      { icon: "Server", title: "Data Security", description: "Encryptie en data loss prevention voor uw gegevens." },
      { icon: "Cloud", title: "Cloud Security", description: "Beveilig uw cloud infrastructuur en applicaties." },
    ],
    benefits: [
      { icon: "Users", title: "Gecertificeerd Team", description: "Ons team is volledig gecertificeerd door Stormshield." },
      { icon: "Award", title: "Competitive Prijzen", description: "Als officieel distributeur bieden wij de beste prijzen." },
      { icon: "Headphones", title: "24/7 Ondersteuning", description: "Technische support wanneer u het nodig heeft." },
    ],
  };

  const hero = sections?.hero || fallback.hero;
  const stats = getExtraDataItems<StatItem>(sections?.stats) || fallback.stats;
  const features = getExtraDataItems<FeatureItem>(sections?.features) || fallback.features;
  const benefits = getExtraDataItems<FeatureItem>(sections?.benefits) || fallback.benefits;
  const about = sections?.about;
  const cta = sections?.cta;

  const productsPath = currentLanguage === "en" ? "/products" : currentLanguage === "fr" ? "/produits" : "/producten";
  const aboutPath = currentLanguage === "en" ? "/about" : currentLanguage === "fr" ? "/a-propos" : "/over-ons";
  const partnerPath = currentLanguage === "en" ? "/become-partner" : currentLanguage === "fr" ? "/devenir-partenaire" : "/partner-worden";

  return (
    <div>
      {/* Floating Advisor Button */}
      <FirewallAdvisorFAB />
      
      {/* Controlled Modal for Hero Card */}
      <FirewallAdvisorModal open={advisorModalOpen} onOpenChange={setAdvisorModalOpen} showTrigger={false} />
      
      {/* Hero Section - min-h ensures stable layout before content loads */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Preload hero image with explicit dimensions to prevent CLS */}
        <img 
          src={heroCybersecurity}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[hsl(var(--hero-background))]/80" />
        
        <div className="container relative z-10 py-20 md:py-32">
          <div className="max-w-3xl space-y-6">
            <span className="inline-block text-primary font-semibold text-sm uppercase tracking-wider">
              {hero.subtitle}
            </span>
            {isLoading ? (
              <Skeleton className="h-16 w-3/4" />
            ) : (
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[hsl(var(--hero-foreground))] leading-tight">
                {hero.title?.split(" ").slice(0, -3).join(" ")}{" "}
                <span className="text-primary">{hero.title?.split(" ").slice(-3).join(" ")}</span>
              </h1>
            )}
            <p className="text-lg md:text-xl text-[hsl(var(--hero-muted))] max-w-2xl">
              {hero.content}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button size="lg" className="text-base px-8" asChild>
                <Link to={hero.button_link || partnerPath}>
                  {hero.button_text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-base px-8 border-white/60 text-white bg-white/10 hover:bg-white/20 hover:border-white hover:text-white" 
                asChild
              >
                <Link to={productsPath}>
                  {currentLanguage === "en" ? "View Products" : currentLanguage === "fr" ? "Voir les Produits" : "Bekijk Producten"}
                </Link>
              </Button>
            </div>

            {/* Firewall Advisor Card */}
            <button
              onClick={() => setAdvisorModalOpen(true)}
              className="mt-8 group relative overflow-hidden rounded-xl border border-primary/30 bg-[hsl(var(--hero-background))]/60 backdrop-blur-sm p-5 text-left transition-all duration-300 hover:border-primary/60 hover:shadow-lg hover:shadow-primary/20 animate-pulse-glow max-w-md"
            >
              <div className="absolute inset-0 overflow-hidden rounded-xl">
                <div className="absolute inset-0 -translate-x-full animate-radar-scan bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
              </div>
              
              <div className="relative flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/20 group-hover:bg-primary/30 transition-colors">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[hsl(var(--hero-foreground))] font-semibold">
                      {currentLanguage === "en" ? "Which firewall suits you?" : currentLanguage === "fr" ? "Quel pare-feu vous convient?" : "Welke firewall past bij jou?"}
                    </span>
                    <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                  </div>
                  <span className="text-[hsl(var(--hero-muted))] text-sm flex items-center gap-1">
                    {currentLanguage === "en" ? "Discover in 30 seconds" : currentLanguage === "fr" ? "Découvrez en 30 secondes" : "Ontdek het in 30 seconden"}
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-primary py-8">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {(stats.length > 0 ? stats : fallback.stats).map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-foreground mb-1">
                  {stat.value}
                </div>
                <div className="text-primary-foreground/80 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-28 bg-card">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <SectionHeading 
                subtitle={about?.subtitle || "Over Cyscom"}
                title={about?.title || "UW PARTNER IN CYBERSECURITY"}
                align="left"
              />
              <p className="text-muted-foreground text-lg mb-6">
                {about?.content || "Cyscom is de officiële Stormshield distributeur voor de Benelux. Met meer dan 15 jaar ervaring in cybersecurity ondersteunen wij MSP's, resellers en enterprise organisaties met de beste beveiligingsoplossingen."}
              </p>
              <div className="space-y-4 mb-8">
                {[
                  currentLanguage === "en" ? "Official Stormshield distributor" : currentLanguage === "fr" ? "Distributeur officiel Stormshield" : "Officiële Stormshield distributeur",
                  currentLanguage === "en" ? "Certified technical support team" : currentLanguage === "fr" ? "Équipe de support technique certifiée" : "Gecertificeerd technisch support team",
                  currentLanguage === "en" ? "Fast delivery and implementation" : currentLanguage === "fr" ? "Livraison et implémentation rapides" : "Snelle levering en implementatie via onze partners",
                  currentLanguage === "en" ? "Training and certification programs" : currentLanguage === "fr" ? "Programmes de formation et certification" : "Training en certificeringsprogramma's",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              <Button size="lg" asChild>
                <Link to={about?.button_link || aboutPath}>
                  {about?.button_text || "Meer Over Ons"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            
            <div className="relative">
              <img 
                src={teamMeeting} 
                alt="Cyscom team"
                className="rounded-lg shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary rounded-lg -z-10 hidden lg:block" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-muted">
        <div className="container">
          <SectionHeading 
            subtitle={sections?.features?.subtitle || "Onze Oplossingen"}
            title={sections?.features?.title || "COMPLETE CYBERSECURITY STACK"}
            description={currentLanguage === "en" ? "From network security to cloud protection - we offer the complete Stormshield product line." : currentLanguage === "fr" ? "De la sécurité réseau à la protection cloud - nous offrons la gamme complète Stormshield." : "Van network security tot cloud protection - wij bieden de complete Stormshield productlijn."}
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(features.length > 0 ? features : fallback.features).map((feature) => {
              const IconComponent = iconMap[feature.icon] || Shield;
              return (
                <Card 
                  key={feature.title} 
                  className="bg-card border-border hover:border-primary/50 hover:shadow-xl transition-all duration-300 group overflow-hidden"
                >
                  <CardHeader className="pb-4">
                    <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <IconComponent className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl text-card-foreground">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                    <Link 
                      to={productsPath} 
                      className="inline-flex items-center gap-2 text-primary font-medium mt-4 hover:gap-3 transition-all"
                    >
                      {currentLanguage === "en" ? "More info" : currentLanguage === "fr" ? "Plus d'info" : "Meer info"}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-card">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative order-2 lg:order-1">
              <img 
                src={datacenter} 
                alt="Datacenter security"
                className="rounded-lg shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-primary rounded-lg -z-10 hidden lg:block" />
            </div>
            
            <div className="order-1 lg:order-2">
              <SectionHeading 
                subtitle={currentLanguage === "en" ? "Why Cyscom" : currentLanguage === "fr" ? "Pourquoi Cyscom" : "Waarom Cyscom"}
                title={currentLanguage === "en" ? "PARTNER BENEFITS" : currentLanguage === "fr" ? "AVANTAGES PARTENAIRES" : "VOORDELEN VOOR PARTNERS"}
                align="left"
              />
              <div className="space-y-8">
                {(benefits.length > 0 ? benefits : fallback.benefits).map((benefit) => {
                  const IconComponent = iconMap[benefit.icon] || Users;
                  return (
                    <div key={benefit.title} className="flex gap-4">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-foreground mb-1">{benefit.title}</h3>
                        <p className="text-muted-foreground">{benefit.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button size="lg" className="mt-8" asChild>
                <Link to={partnerPath}>
                  {currentLanguage === "en" ? "Become Partner" : currentLanguage === "fr" ? "Devenir Partenaire" : "Word Partner"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[hsl(var(--hero-background))] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }} />
        </div>
        
        <div className="container relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-[hsl(var(--hero-foreground))]">
            {cta?.title || (currentLanguage === "en" ? "READY TO GET STARTED?" : currentLanguage === "fr" ? "PRÊT À COMMENCER?" : "KLAAR OM TE STARTEN?")}
          </h2>
          <p className="text-[hsl(var(--hero-muted))] max-w-2xl mx-auto mb-10 text-lg">
            {cta?.content || (currentLanguage === "en" ? "Contact us or sign up as a partner. Our team is ready to help you with the best security solutions." : currentLanguage === "fr" ? "Contactez-nous ou inscrivez-vous en tant que partenaire. Notre équipe est prête à vous aider." : "Neem contact met ons op of meld je direct aan als partner. Ons team staat klaar om je te helpen met de beste security oplossingen.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="text-base px-8" asChild>
              <Link to={partnerPath}>
                {currentLanguage === "en" ? "Become Partner" : currentLanguage === "fr" ? "Devenir Partenaire" : "Partner Worden"}
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-base px-8 border-white/60 text-white bg-white/10 hover:bg-white/20 hover:border-white hover:text-white" 
              asChild
            >
              <Link to="/contact">
                {cta?.button_text || (currentLanguage === "en" ? "Contact Us" : currentLanguage === "fr" ? "Contactez-nous" : "Neem Contact Op")}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
